import { useCallback, useEffect, useRef, useState } from "react";
import {
  createMeeting,
  finalizeMeeting,
  getMeeting,
  meetingAudioUrl,
  uploadRecordingChunk,
} from "../lib/meetingApi";
import {
  createRecordingChunkStore,
  type RecoverableRecordingMeeting,
  type RecordingChunkStore,
} from "../lib/recordingChunkStore";

export type MeetingRecordingStatus =
  | "idle"
  | "preparing"
  | "recording"
  | "stopping"
  | "uploading"
  | "finalizing"
  | "ready"
  | "error";

export type MeetingRecordingErrorCode =
  | "start"
  | "unsupported"
  | "microphone"
  | "upload"
  | "recorder"
  | "no-audio"
  | "finalize";

export type UseMeetingRecordingResult = {
  status: MeetingRecordingStatus;
  meetingId: string | null;
  elapsedSeconds: number;
  pendingChunks: number;
  lastAcknowledgedSequence: number | null;
  playbackUrl: string | null;
  error: MeetingRecordingErrorCode | null;
  warning: "storage" | null;
  recoverableMeeting: Pick<RecoverableRecordingMeeting, "id" | "title" | "createdAt"> | null;
  recoverableMeetingCount: number;
  isActive: boolean;
  start(stream: MediaStream, title: string): Promise<void>;
  stopAndFinalize(): Promise<void>;
  recoverAndFinalize(): Promise<void>;
  discardRecoverable(): Promise<void>;
  reset(): void;
};

const CHUNK_TIMESLICE_MS = 10_000;
const RECORDING_AUDIO_BITS_PER_SECOND = 48_000;

type PendingChunk = {
  blob: Blob;
  sequence: number;
  sha256?: string;
};

export function useMeetingRecording(csrfToken: string | null): UseMeetingRecordingResult {
  const [status, setStatus] = useState<MeetingRecordingStatus>("idle");
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [pendingChunks, setPendingChunks] = useState(0);
  const [lastAcknowledgedSequence, setLastAcknowledgedSequence] = useState<number | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [error, setError] = useState<MeetingRecordingErrorCode | null>(null);
  const [warning, setWarning] = useState<"storage" | null>(null);
  const [recoverableMeetings, setRecoverableMeetings] = useState<RecoverableRecordingMeeting[]>([]);
  const recoverableMeeting = recoverableMeetings[0] ?? null;
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingTrackRef = useRef<MediaStreamTrack | null>(null);
  const meetingIdRef = useRef<string | null>(null);
  const sequenceRef = useRef(0);
  const uploadChainRef = useRef<Promise<void>>(Promise.resolve());
  const uploadErrorRef = useRef<unknown>(null);
  const pendingChunksRef = useRef(new Map<number, PendingChunk>());
  const startedAtRef = useRef<number | null>(null);
  const stoppingRef = useRef<Promise<void> | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const chunkStoreRef = useRef<RecordingChunkStore | null>(null);
  const persistenceEnabledRef = useRef(true);

  const chunkStore = useCallback(() => {
    chunkStoreRef.current ??= createRecordingChunkStore();
    return chunkStoreRef.current;
  }, []);

  const markPersistenceUnavailable = useCallback(() => {
    persistenceEnabledRef.current = false;
    chunkStoreRef.current?.close();
    chunkStoreRef.current = null;
    setWarning("storage");
  }, []);

  const uploadPendingChunk = useCallback(async (
    activeMeetingId: string,
    chunk: PendingChunk,
  ) => {
    if (!csrfToken) throw new RecordingFailure("start", "Authentication is required to record");
    chunk.sha256 ??= await sha256Blob(chunk.blob);
    await retryUpload(() => uploadRecordingChunk({
      csrfToken,
      meetingId: activeMeetingId,
      sequence: chunk.sequence,
      blob: chunk.blob,
      sha256: chunk.sha256!,
    }));
    if (persistenceEnabledRef.current) {
      try {
        await chunkStore().deleteAcknowledgedChunk(activeMeetingId, chunk.sequence);
      } catch {
        markPersistenceUnavailable();
      }
    }
    if (pendingChunksRef.current.get(chunk.sequence) === chunk) {
      pendingChunksRef.current.delete(chunk.sequence);
    }
    setPendingChunks(pendingChunksRef.current.size);
    setLastAcknowledgedSequence(previous => (
      previous === null ? chunk.sequence : Math.max(previous, chunk.sequence)
    ));
    if (pendingChunksRef.current.size === 0) {
      uploadErrorRef.current = null;
      setError(null);
    }
  }, [chunkStore, csrfToken, markPersistenceUnavailable]);

  const start = useCallback(async (stream: MediaStream, title: string) => {
    if (!csrfToken) throw new RecordingFailure("start", "Authentication is required to record");
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      throw new Error("A recording is already active");
    }
    setStatus("preparing");
    setError(null);
    setWarning(null);
    setPlaybackUrl(null);
    setPendingChunks(0);
    setLastAcknowledgedSequence(null);
    sequenceRef.current = 0;
    uploadChainRef.current = Promise.resolve();
    uploadErrorRef.current = null;
    pendingChunksRef.current.clear();
    persistenceEnabledRef.current = true;
    let createdMeetingId: string | null = null;

    try {
      if (typeof MediaRecorder === "undefined") {
        throw new RecordingFailure("unsupported", "Recording is not supported by this browser");
      }
      const sourceTrack = stream.getAudioTracks()[0];
      if (!sourceTrack || sourceTrack.readyState !== "live") {
        throw new RecordingFailure("microphone", "The selected microphone is unavailable");
      }
      const recordingTrack = sourceTrack.clone();
      recordingTrackRef.current = recordingTrack;
      const recordingStream = new MediaStream([recordingTrack]);
      const mimeType = preferredRecordingMimeType();
      const recorder = new MediaRecorder(recordingStream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: RECORDING_AUDIO_BITS_PER_SECOND,
      });
      const id = crypto.randomUUID();
      await createMeeting(csrfToken, {
        id,
        title: title.trim(),
        consentConfirmed: true,
      });
      createdMeetingId = id;
      recorderRef.current = recorder;
      meetingIdRef.current = id;
      setMeetingId(id);
      try {
        await chunkStore().putMeeting({
          id,
          title: title.trim(),
          mimeType: recorder.mimeType || mimeType || "audio/webm",
          nextSequence: 0,
          state: "recording",
          lastSequence: null,
          createdAt: Date.now(),
        });
      } catch {
        markPersistenceUnavailable();
      }

      recorder.addEventListener("dataavailable", event => {
        if (!event.data.size) return;
        const sequence = sequenceRef.current++;
        const chunk = { blob: event.data, sequence };
        pendingChunksRef.current.set(sequence, chunk);
        setPendingChunks(pendingChunksRef.current.size);
        const persisted = persistenceEnabledRef.current
          ? chunkStore().putChunk({ meetingId: id, sequence, blob: event.data }).catch(() => {
              markPersistenceUnavailable();
            })
          : Promise.resolve();
        uploadChainRef.current = uploadChainRef.current
          .then(() => persisted)
          .then(() => uploadPendingChunk(id, chunk))
          .catch(uploadError => {
            uploadErrorRef.current ??= uploadError;
            setError("upload");
          });
      });
      recorder.addEventListener("error", () => {
        setError("recorder");
        setStatus("error");
      });
      recordingTrack.addEventListener("ended", () => {
        if (recorderRef.current !== recorder || recorder.state === "inactive") return;
        setError("microphone");
        setStatus("error");
        try {
          recorder.stop();
        } catch {
          // The persistent error is already visible and finalization can be retried.
        }
      });

      recorder.start(CHUNK_TIMESLICE_MS);
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      setStatus("recording");
    } catch (startError) {
      recordingTrackRef.current?.stop();
      recordingTrackRef.current = null;
      recorderRef.current = null;
      meetingIdRef.current = null;
      setMeetingId(null);
      if (createdMeetingId && persistenceEnabledRef.current) {
        try {
          await chunkStore().deleteMeeting(createdMeetingId);
        } catch {
          markPersistenceUnavailable();
        }
      }
      setStatus("error");
      setError(startError instanceof RecordingFailure ? startError.code : "start");
      throw startError;
    }
  }, [chunkStore, csrfToken, markPersistenceUnavailable, uploadPendingChunk]);

  const stopAndFinalize = useCallback(async () => {
    if (stoppingRef.current) return stoppingRef.current;
    const operation = (async () => {
      const recorder = recorderRef.current;
      const activeMeetingId = meetingIdRef.current;
      if (!recorder || !activeMeetingId || !csrfToken) {
        throw new RecordingFailure("finalize", "The active recording is unavailable");
      }

      setError(null);
      setStatus("stopping");
      try {
        if (recorder.state !== "inactive") {
          await new Promise<void>((resolve, reject) => {
            recorder.addEventListener("stop", () => resolve(), { once: true });
            recorder.addEventListener("error", () => reject(
              new RecordingFailure("recorder", "The browser recorder failed"),
            ), { once: true });
            recorder.stop();
          });
        }
      } finally {
        recordingTrackRef.current?.stop();
        recordingTrackRef.current = null;
      }

      setStatus("uploading");
      await uploadChainRef.current;
      const lastSequence = sequenceRef.current - 1;
      if (persistenceEnabledRef.current) {
        try {
          await chunkStore().markStopped(activeMeetingId, lastSequence >= 0 ? lastSequence : null);
        } catch {
          markPersistenceUnavailable();
        }
      }
      uploadErrorRef.current = null;
      for (const chunk of [...pendingChunksRef.current.values()].sort((a, b) => (
        a.sequence - b.sequence
      ))) {
        try {
          await uploadPendingChunk(activeMeetingId, chunk);
        } catch (uploadError) {
          uploadErrorRef.current = uploadError;
          break;
        }
      }
      if (pendingChunksRef.current.size > 0) {
        throw new RecordingFailure("upload", "Recording chunks are still pending");
      }
      if (lastSequence < 0) {
        throw new RecordingFailure("no-audio", "The recording did not produce any audio");
      }

      setStatus("finalizing");
      const result = await finalizeMeeting(csrfToken, activeMeetingId, lastSequence);
      if (result.missingSequences.length) {
        throw new RecordingFailure("finalize", "Recording chunks are missing");
      }
      setPlaybackUrl(result.playbackReady ? meetingAudioUrl(activeMeetingId) : null);
      setError(null);
      setStatus("ready");
      recorderRef.current = null;
      if (persistenceEnabledRef.current) {
        try {
          await chunkStore().deleteMeeting(activeMeetingId);
        } catch {
          markPersistenceUnavailable();
        }
      }
    })().catch(stopError => {
      setStatus("error");
      setError(stopError instanceof RecordingFailure ? stopError.code : "finalize");
      throw stopError;
    }).finally(() => {
      stoppingRef.current = null;
    });
    stoppingRef.current = operation;
    return operation;
  }, [chunkStore, csrfToken, markPersistenceUnavailable, uploadPendingChunk]);

  const recoverAndFinalize = useCallback(async () => {
    if (stoppingRef.current) return stoppingRef.current;
    const candidate = recoverableMeeting;
    if (!candidate || !csrfToken) return;

    const operation = (async () => {
      setError(null);
      setWarning(null);
      setStatus("uploading");
      setMeetingId(candidate.id);
      meetingIdRef.current = candidate.id;
      persistenceEnabledRef.current = true;

      const remoteMeeting = await getMeeting(candidate.id);
      if (remoteMeeting.playbackReady) {
        setPlaybackUrl(meetingAudioUrl(candidate.id));
        setStatus("ready");
        try {
          await chunkStore().deleteMeeting(candidate.id);
          setRecoverableMeetings(meetings => meetings.filter(meeting => meeting.id !== candidate.id));
        } catch {
          markPersistenceUnavailable();
        }
        return;
      }

      const storedChunks = await chunkStore().listChunks(candidate.id);
      pendingChunksRef.current.clear();
      for (const storedChunk of storedChunks) {
        pendingChunksRef.current.set(storedChunk.sequence, {
          blob: storedChunk.blob,
          sequence: storedChunk.sequence,
        });
      }
      sequenceRef.current = candidate.nextSequence;
      setPendingChunks(pendingChunksRef.current.size);
      setLastAcknowledgedSequence(null);

      for (const chunk of [...pendingChunksRef.current.values()].sort((a, b) => (
        a.sequence - b.sequence
      ))) {
        await uploadPendingChunk(candidate.id, chunk);
      }

      const lastSequence = candidate.lastSequence ?? candidate.nextSequence - 1;
      if (lastSequence < 0) {
        throw new RecordingFailure("no-audio", "The recovered recording has no audio");
      }
      try {
        await chunkStore().markStopped(candidate.id, lastSequence);
      } catch {
        markPersistenceUnavailable();
      }

      setStatus("finalizing");
      const result = await finalizeMeeting(csrfToken, candidate.id, lastSequence);
      if (result.missingSequences.length) {
        throw new RecordingFailure("finalize", "Recovered recording chunks are missing");
      }
      setPlaybackUrl(result.playbackReady ? meetingAudioUrl(candidate.id) : null);
      setError(null);
      setStatus("ready");
      if (persistenceEnabledRef.current) {
        try {
          await chunkStore().deleteMeeting(candidate.id);
          setRecoverableMeetings(meetings => meetings.filter(meeting => meeting.id !== candidate.id));
        } catch {
          markPersistenceUnavailable();
        }
      }
    })().catch(recoveryError => {
      setStatus("error");
      setError(recoveryError instanceof RecordingFailure ? recoveryError.code : "finalize");
      throw recoveryError;
    }).finally(() => {
      stoppingRef.current = null;
    });
    stoppingRef.current = operation;
    return operation;
  }, [chunkStore, csrfToken, markPersistenceUnavailable, recoverableMeeting, uploadPendingChunk]);

  const discardRecoverable = useCallback(async () => {
    const candidate = recoverableMeeting;
    if (!candidate || stoppingRef.current) return;
    try {
      await chunkStore().deleteMeeting(candidate.id);
      setRecoverableMeetings(meetings => meetings.filter(meeting => meeting.id !== candidate.id));
      if (meetingIdRef.current === candidate.id) {
        meetingIdRef.current = null;
        setMeetingId(null);
        setPlaybackUrl(null);
        setPendingChunks(0);
        setLastAcknowledgedSequence(null);
        pendingChunksRef.current.clear();
        setError(null);
        setStatus("idle");
      }
    } catch (discardError) {
      markPersistenceUnavailable();
      setError("finalize");
      setStatus("error");
      throw discardError;
    }
  }, [chunkStore, markPersistenceUnavailable, recoverableMeeting]);

  const reset = useCallback(() => {
    if (recorderRef.current?.state !== "inactive") return;
    meetingIdRef.current = null;
    setMeetingId(null);
    setPlaybackUrl(null);
    setError(null);
    setWarning(null);
    setElapsedSeconds(0);
    setPendingChunks(0);
    setLastAcknowledgedSequence(null);
    pendingChunksRef.current.clear();
    uploadErrorRef.current = null;
    setStatus("idle");
  }, []);

  useEffect(() => {
    if (status !== "recording" || !startedAtRef.current) return;
    const interval = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current!) / 1000));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [status]);

  useEffect(() => {
    const retryPendingAudio = () => {
      const activeMeetingId = meetingIdRef.current;
      if (!activeMeetingId || pendingChunksRef.current.size === 0) return;
      uploadChainRef.current = uploadChainRef.current
        .then(async () => {
          for (const chunk of [...pendingChunksRef.current.values()].sort((a, b) => (
            a.sequence - b.sequence
          ))) {
            if (pendingChunksRef.current.get(chunk.sequence) !== chunk) continue;
            await uploadPendingChunk(activeMeetingId, chunk);
          }
        })
        .catch(uploadError => {
          uploadErrorRef.current ??= uploadError;
          setError("upload");
        });
    };
    window.addEventListener("online", retryPendingAudio);
    return () => window.removeEventListener("online", retryPendingAudio);
  }, [uploadPendingChunk]);

  useEffect(() => {
    if (!csrfToken) {
      setRecoverableMeetings([]);
      return;
    }
    let cancelled = false;
    void chunkStore().listUnfinishedMeetings()
      .then(async meetings => {
        const emptyMeetings = meetings.filter(meeting => meeting.nextSequence === 0);
        await Promise.all(emptyMeetings.map(meeting => (
          chunkStore().deleteMeeting(meeting.id).catch(() => undefined)
        )));
        if (cancelled) return;
        const recoverable = meetings.filter(meeting => meeting.nextSequence > 0);
        setRecoverableMeetings(recoverable);
      })
      .catch(() => {
        // Recording start will surface a recovery-storage warning if persistence is unavailable.
      });
    return () => {
      cancelled = true;
    };
  }, [chunkStore, csrfToken]);

  useEffect(() => {
    const needsWakeLock = status === "recording"
      || status === "stopping"
      || status === "uploading"
      || status === "finalizing"
      || (status === "error" && meetingId !== null);
    if (!needsWakeLock || !navigator.wakeLock) return;
    let cancelled = false;

    const requestWakeLock = async () => {
      if (cancelled || document.visibilityState !== "visible" || wakeLockRef.current) return;
      try {
        const sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) {
          await sentinel.release();
          return;
        }
        wakeLockRef.current = sentinel;
        sentinel.addEventListener("release", () => {
          if (wakeLockRef.current === sentinel) wakeLockRef.current = null;
        }, { once: true });
      } catch {
        // Wake Lock is an enhancement; recording and navigation protection remain active.
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void requestWakeLock();
    };
    void requestWakeLock();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      const sentinel = wakeLockRef.current;
      wakeLockRef.current = null;
      void sentinel?.release().catch(() => undefined);
    };
  }, [meetingId, status]);

  useEffect(() => {
    if (
      status !== "recording"
      && status !== "stopping"
      && status !== "uploading"
      && status !== "finalizing"
      && !(status === "error" && meetingId)
    ) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [meetingId, status]);

  useEffect(() => () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") recorder.stop();
    recordingTrackRef.current?.stop();
    chunkStoreRef.current?.close();
  }, []);

  return {
    status,
    meetingId,
    elapsedSeconds,
    pendingChunks,
    lastAcknowledgedSequence,
    playbackUrl,
    error,
    warning,
    recoverableMeeting,
    recoverableMeetingCount: recoverableMeetings.length,
    isActive: status === "preparing"
      || status === "recording"
      || status === "stopping"
      || status === "uploading"
      || status === "finalizing"
      || (status === "error" && meetingId !== null),
    start,
    stopAndFinalize,
    recoverAndFinalize,
    discardRecoverable,
    reset,
  };
}

function preferredRecordingMimeType(): string | undefined {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
  ];
  return candidates.find(candidate => MediaRecorder.isTypeSupported(candidate));
}

async function sha256Blob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}

async function retryUpload<T>(operation: () => Promise<T>): Promise<T> {
  const delays = [0, 500, 1_500];
  let lastError: unknown;
  for (const delay of delays) {
    if (delay) await new Promise(resolve => window.setTimeout(resolve, delay));
    try {
      return await operation();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

class RecordingFailure extends Error {
  constructor(
    readonly code: MeetingRecordingErrorCode,
    message: string,
  ) {
    super(message);
  }
}
